import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ToyList from './components/toys/ToyList';
import ToyDetail from './components/toys/ToyDetail';
import WishList from './components/toys/WishList';
import WishDetail from './components/toys/WishDetail';
import UserSettings from './components/settings/UserSettings';
import CustomDataManager from './components/settings/CustomDataManager';
import MyFigureCollectionImport from './components/import/MyFigureCollectionImport';
import Navbar from './components/layout/Navbar';
import LandingPage from './components/landing/LandingPage';
import CollectionInsights from './components/insights/CollectionInsights';
import PublicCollectionPage from './components/public/PublicCollectionPage';
import CollectionExport from './components/export/CollectionExport';

const theme = createTheme({
  palette: {
    primary: {
      main: '#6366f1', // Indigo
      light: '#818cf8',
      dark: '#4f46e5',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#8b5cf6', // Purple
      light: '#a78bfa',
      dark: '#7c3aed',
      contrastText: '#ffffff',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    text: {
      primary: '#1f2937',
      secondary: '#6b7280',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.04)',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-2px)',
            transition: 'all 0.3s ease',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          backgroundColor: '#ffffff',
          color: '#212121',
        },
      },
    },
  },
});

function PrivateRoute({ children }) {
  const { currentUser } = useAuth();
  return currentUser ? children : <Navigate to="/login" />;
}

function AppContent() {
  const { currentUser } = useAuth();
  
  return (
    <Router>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {currentUser && <Navbar />}
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/public/:userId" element={<PublicCollectionPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route 
            path="/toys" 
            element={
              <PrivateRoute>
                <ToyList />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/toy/:id" 
            element={
              <PrivateRoute>
                <ToyDetail />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/settings" 
            element={
              <PrivateRoute>
                <UserSettings />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/export" 
            element={
              <PrivateRoute>
                <CollectionExport />
              </PrivateRoute>
            } 
          />
          <Route
            path="/import/myfigurecollection"
            element={
              <PrivateRoute>
                <MyFigureCollectionImport />
              </PrivateRoute>
            }
          />
          <Route 
            path="/settings/custom-data" 
            element={
              <PrivateRoute>
                <CustomDataManager />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/insights" 
            element={
              <PrivateRoute>
                <CollectionInsights />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/wishlist" 
            element={
              <PrivateRoute>
                <WishList />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/wish/:id" 
            element={
              <PrivateRoute>
                <WishDetail />
              </PrivateRoute>
            } 
          />
        </Routes>
      </ThemeProvider>
    </Router>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <LanguageProvider>
          <AppContent />
        </LanguageProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App; 
